import { Component, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Location } from '@angular/common';

import * as _ from 'lodash';

import { PartsPluginService } from './parts-plugin.service';
import { BaseModalPageComponent } from '../../base-modal.component';
import { PartMenuComponent } from '../../menus/index';
import { Inspection } from '../../shared/models/index';
import { ConfirmComponent } from '../../modal/confirm.component';
import { ModalComponent } from '../../modal/modal.component';
import { Currency, SelectorComponent } from '../../utils';
import { PartsViewModalComponent } from './partsview-modal.component';
import { SnackBarService } from '../../shared/services/snack-bar.service';
import { MachineSelectorPluginService } from '../machineselector/machineselector-plugin.service';

@Component({
    selector: 'parts-edit',
    templateUrl: './parts-edit.component.html',
    standalone: false
})
export class PartsEditPageComponent extends BaseModalPageComponent implements OnInit {
  readonly currencies = Object.values(Currency);
  inspectionId: string;
  pointId: string;
  pluginId: string;
  query = '';
  readonlyPrices = false;
  public enableSearch = true;
  public filter: any = {};
  public plugin: any;
  public inspectionPartsView: any[];
  public pointParts: any[];
  public requiredParts: any;
  public inspection: Inspection;
  public processing = false;
  private partToDelete: any;
  public menuComponent = PartMenuComponent;
  private currentMachineSerial: string;

  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;
  @ViewChild('deletePartConfirm', { static: true })
  deletePartConfirm: ConfirmComponent;
  @ViewChild('equipmentModal', { static: true })
  equipmentModal: ModalComponent;
  @ViewChild('catalogModal', { static: true })
  catalogModal: ModalComponent;
  @ViewChild('partsviewModal', { static: true })
  partsviewModal: ModalComponent;
  @ViewChild('partsViewEquipmentModal', { static: true })
  partsViewEquipmentModal: ModalComponent;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private service: PartsPluginService,
    location: Location,
    private cdRef: ChangeDetectorRef,
    private snackbar: SnackBarService,
    private machineService: MachineSelectorPluginService
  ) {
    super(location);
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) {
        this.router.navigate([this.inspectionsRoute]);
      }
      this.inspectionId = id;

      const pointId = params['pointId'];
      if (pointId == null) {
        this.router.navigate([this.inspectionsRoute, this.inspectionId]);
      }

      this.pointId = pointId;

      const pluginId = params['pluginId'];
      if (pluginId == null) {
        this.goToBack();
      }

      this.pluginId = pluginId;

      this.initData();
    });
  }

  save() {
    this.processing = true;
    this.service
      .updateParts(
        this.inspectionId,
        this.pointId,
        this.pluginId,
        _.filter(this.pointParts, (p: any) => p.Quantity > 0 || ((p.Quotation || p.Currency) && this.pointId === '-1')),
        this.requiredParts
      )
      .subscribe((el) => {
        this.processing = false;
        this.goToBack();
      });
  }

  cancel() {
    this.goToBack();
  }

  ngOnInit() {
    this.initRoute();
  }

  initData() {
    this.readonlyPrices = this.pointId != '-1';
    this.service.getPlugin(this.inspectionId, this.pluginId).subscribe((data) => {
      this.plugin = data;
      this.inspection = data.inspection;
      this.requiredParts =
        data && data.config && data.config.PointParts && data.config.PointParts[this.pointId]
          ? data.config.PointParts[this.pointId]
          : {};
      if (!this.requiredParts.PointId) {
        this.requiredParts.PointId = this.pointId;
      }
      this.inspectionPartsView = data && data.config && data.config.Basket ? data.config.Basket.PartsView : [];
      this.getPointParts();
      this.cdRef.detectChanges();
    });
  }

  getPointParts() {
    if (this.plugin.config.Parts && this.pointId !== '-1') {
      this.pointParts = this.plugin.config.Parts.filter((p) => p.PointId === this.pointId);
      this.pointParts = _.map(this.pointParts, (p: any) => {
        return {
          Id: p.Id,
          Quantity: p.Quantity,
          PointId: this.pointId,
          Page: p.Page,
          Position: p.Position,
          PartName: p.Name,
          Name: p.Name,
          Quotation: p.Quotation,
          Currency: p.Currency,
          Total: p.Total,
          EquipmentId: p.EquipmentId != null ? p.EquipmentId : p.Machine,
          FromPartsView: p.FromPartsView
        };
      });
    }

    const tranformedParts = _.map(this.inspectionPartsView, (p: any) => {
      return {
        Id: p.Parts,
        Quantity: 0,
        PointId: this.pointId,
        Page: p.Page,
        Position: p.Position,
        PartName: p.Name,
        Name: p.Name,
        Total: p.Total,
        Quotation: p.Quotation,
        Currency: p.Currency,
        EquipmentId: p.EquipmentId != null ? p.EquipmentId : p.Machine,
        FromPartsView: p.FromPartsView
      };
    });

    this.pointParts = _.unionBy(
      this.pointParts,
      tranformedParts,
      (el: any) => `${el.Id}-${el.Page}-${el.Position}-${el.Name}`
    );
    this.processData();
  }

  processData() {
    _.forEach(this.pointParts, (el) => {
      if (!el.search) {
        el.search = (query: string): boolean =>
          el.Name == null ||
          el.Name.toLowerCase().includes(query.toLowerCase()) ||
          el.Id.toLowerCase().includes(query.toLowerCase());
      }
    });
  }

  updateQuantity(part: any, quantity: number) {
    if (part.Quantity > 0 || (part.Quantity === 0 && quantity > 0)) {
      part.Quantity = part.Quantity + quantity;
    }
  }

  checkPartInPoints(part: any) {
    return this.plugin.config.Parts.some(
      (p) => p.PointId != -1 && p.PointId !== this.pointId && part.Name === p.Name && part.Id === p.Id
    );
  }

  add() {
    const numbers = this.getMachineSerials();
    if (numbers.length > 1) {
      this.openEquipmentSelector(numbers);
    } else {
      this.router.navigate([
        this.inspectionsRoute,
        this.inspectionId,
        'points',
        this.pointId,
        'plugins',
        this.pluginId,
        'parts',
        'addPart',
        numbers[0] ? numbers[0] : ''
      ]);
    }
  }

  private getMachineSerials() {
    const inspection = this.plugin.inspection;
    const numbers = [];
    if (inspection.data && inspection.data.machine && inspection.data.machine.Serial) {
      numbers.push(...inspection.data.machine.Serial);
    }
    return numbers;
  }

  editPart(part) {
    this.router.navigate([
      this.inspectionsRoute,
      this.inspectionId,
      'points',
      this.pointId,
      'plugins',
      this.pluginId,
      'parts',
      'edit',
      `${part.Id}-${part.Page}-${part.Position}-${part.PartName}`
    ]);
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        // user clicked on "open partsview"
        this.chooseEquipmentSerial();
        break;
      case 3:
        this.openDeleteConfirm();
        break;
      case 10:
        this.gotoEditJsonPage();
        break;
      case 11:
        this.gotoEditPage();
        break;
      default:
    }
  }

  gotoEditJsonPage() {
    return this.router.navigate([
      this.inspectionsRoute,
      this.inspectionId,
      'points',
      this.pointId,
      'plugins',
      this.pluginId,
      'editjson'
    ]);
  }

  gotoEditPage() {
    return this.router.navigate([
      this.inspectionsRoute,
      this.inspectionId,
      'points',
      this.pointId,
      'plugins',
      this.pluginId,
      'edit'
    ]);
  }

  updateRequired() {
    this.requiredParts.NeedParts = !this.requiredParts.NeedParts;
  }

  isRequired() {
    return this.requiredParts.NeedParts;
  }

  copyTotalValue(part) {
    if (part.Total > 0) {
      part.Quantity = part.Total;
    }
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  openDeletePartConfirm(part) {
    if (!this.checkPartInPoints(part)) {
      this.deletePartConfirm.open();
      this.partToDelete = part;
    }
  }

  deletePlugin(e: any) {
    this.service.deletePlugin(this.inspectionId, this.pointId, this.pluginId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }

  deletePart(e: any) {
    if (!this.checkPartInPoints(this.partToDelete)) {
      this.processing = true;
      this.service.deletePart(this.inspectionId, this.pointId, this.pluginId, this.partToDelete).subscribe((plugin) => {
        this.pointParts = this.pointParts.filter(
          (p) => !(p.Id === this.partToDelete.Id && p.Name === this.partToDelete.Name)
        );
        this.service.updateIcons(
          this.inspectionId,
          this.pointId,
          this.pointParts.filter((p) => p.Quantity > 0)
        );
        this.processing = false;
      });
    }
  }

  public catalogSelected(item: any): void {
    this.openPartsViewModal(item.value, this.currentMachineSerial);
  }

  public equipmentNumberSelected(item: any): void {
    this.router.navigate([
      this.inspectionsRoute,
      this.inspectionId,
      'points',
      this.pointId,
      'plugins',
      this.pluginId,
      'parts',
      'addPart',
      item.value
    ]);
  }

  partsviewModalClosed(parts: any) {
    if (parts?.length) {
      console.log('imported parts', parts);
      this.service.setImportedParts(parts);
      this.router.navigate([
        this.inspectionsRoute,
        this.inspectionId,
        'points',
        this.pointId,
        'plugins',
        this.pluginId,
        'parts',
        'import'
      ]);
    }
  }

  private openEquipmentSelector(numbers: string[]): void {
    this.equipmentModal.modalFooter = false;
    this.equipmentModal.modalHeader = false;
    this.equipmentModal.modalMessage = false;
    this.equipmentModal.open(SelectorComponent, {
      values: numbers
    });
  }

  private openCatalogSelector(values: string[]): void {
    this.catalogModal.modalFooter = false;
    this.catalogModal.modalHeader = false;
    this.catalogModal.modalMessage = false;
    this.catalogModal.open(SelectorComponent, {
      values
    });
  }

  private openPartsViewModal(uid: string, machineSerial: string) {
    this.partsviewModal.hasClassModal = true;
    const inspection = this.plugin.inspection;
    if (inspection?.data?.machine?.PartsBook || inspection?.data?.machine?.ServerPartsBook) {
      this.partsviewModal.open(PartsViewModalComponent, {
        uid,
        machineSerial
      });
      return;
    }
    this.snackbar.info('plugins.parts.no-parts-book');
  }

  private chooseEquipmentSerial() {
    const machineSelectorPlugin = this.inspection.plugins.find((p) => p.pluginType === 'machineselector');
    this.machineService.checkPartsBook(this.inspectionId, machineSelectorPlugin?.id).subscribe(() => {
      const numbers = this.getMachineSerials();
      if (numbers.length > 1) {
        this.openPartsViewEquipmentSelector(numbers);
      } else {
        this.chooseCatalog(numbers[0] || '');
      }
    });
  }

  private chooseCatalog(machineSerial: string) {
    const inspection = this.plugin.inspection;
    const partsBook: string =
      inspection?.data?.machine?.PartsBook +
      (inspection?.data?.machine?.ServerPartsBook ? `,${inspection.data.machine.ServerPartsBook}` : '');
    if (!partsBook) {
      this.snackbar.info('plugins.parts.no-parts-book');
      return;
    }
    const catalog = [...new Set(partsBook.replace(/\s+/g, '').split(','))];
    if (catalog.length === 1) {
      this.openPartsViewModal(catalog[0], machineSerial);
      return;
    }

    this.currentMachineSerial = machineSerial;
    this.openCatalogSelector(catalog);
  }


  private openPartsViewEquipmentSelector(numbers: string[]): void {
    this.partsViewEquipmentModal.modalFooter = false;
    this.partsViewEquipmentModal.modalHeader = false;
    this.partsViewEquipmentModal.modalMessage = false;
    this.partsViewEquipmentModal.open(SelectorComponent, {
      values: numbers
    });
  }

  public partsViewEquipmentNumberSelected(item: any): void {
    this.chooseCatalog(item.value);
  }


}
